'use client';

import type { BlockTypeDefinition } from '@klickibunti/pagebuilder-react';
import { createContext, useContext, type ComponentType } from 'react';
import { CampaignVariant, useCampaign } from './campaign';
import { lookupKeyFor } from './iframe/orchestrateBlocks';
import { useResolvedFor } from './iframe/ResolvedMapProvider';
import {
  WIDGET_REGISTRY,
  WIDGET_BY_TYPE,
  type AnyFrontendWidget,
} from '@/src/widgets/registry';
import type { AnyBlockDto } from '@/src/widgets/types';

export type { BlockTypeDefinition } from '@klickibunti/pagebuilder-react';

// Adapter: until the §4 orchestrator lands and supplies a Resolved map,
// SlotRenderer still passes raw `block: BlockDto` to each component. For
// inline widgets this is correct (resolveSync is identity → Resolved = Config).
// For lookup widgets, the wrapper renders `null` until the orchestrator wires
// up the Resolved data via context.
function widgetToBlockComponent(
  widget: AnyFrontendWidget,
): ComponentType<{ block: AnyBlockDto }> {
  const {
    ComponentA,
    ComponentB,
    ComponentC,
    resolveSync,
    resolveFallback,
    componentType,
  } = widget;
  function BlockAdapter({ block }: { block: AnyBlockDto }) {
    const campaign = useCampaign();
    const Comp =
      campaign === CampaignVariant.C
        ? (ComponentC ?? ComponentA)
        : campaign === CampaignVariant.B
          ? (ComponentB ?? ComponentA)
          : ComponentA;
    // Lookup widget — Resolved data comes from the iframe orchestrator
    // (§4) via ResolvedMapProvider. Render `null` until it lands, unless the
    // widget can render from its config (`resolveFallback`); widgets that find
    // no data hide themselves per concept §3.4.
    // Hooks must be called unconditionally — call useResolvedFor here and
    // branch on resolveSync below.
    const resolvedFromContext = useResolvedFor(
      lookupKeyFor({ componentType, id: block.id }),
    );
    // `id` stays in the config: the Contact Widget sends it back to
    // `POST /api/forms` so the backend can look up *that widget's* delivery
    // target without trusting a recipient address from the request (SH-455).
    const { componentType: _ct, ...config } = block;
    void _ct;
    if (!resolveSync) {
      const fromContext = resolvedFromContext ?? resolveFallback?.(config);
      if (!fromContext) {
        return null;
      }
      // Merge block-level phone fields (resolved by the backend's
      // PhoneResolutionService) into the resolved data so lookup widgets
      // can access them alongside their resolver output.
      const resolved =
        block.phone || block.matelsoPhoneLabel
          ? {
              ...(fromContext as Record<string, unknown>),
              phone: block.phone,
              matelsoPhoneLabel: block.matelsoPhoneLabel,
            }
          : fromContext;
      return <Comp resolved={resolved} />;
    }
    const resolved = resolveSync(config);
    return <Comp resolved={resolved} />;
  }
  BlockAdapter.displayName = `BlockAdapter(${componentType})`;
  return BlockAdapter;
}

// --- Block-type definitions for the BlockTypePickerModal (klickibunti) ---

export const BLOCK_REGISTRY: BlockTypeDefinition[] = WIDGET_REGISTRY.map(
  (w) => ({
    component: w.componentType,
    displayName: w.displayName,
    defaultValues: w.defaultValues ?? {},
  }),
);

export function getBlockDefinition(
  component: string,
): BlockTypeDefinition | undefined {
  return BLOCK_REGISTRY.find((b) => b.component === component);
}

// --- Component map (used by SlotRenderer via context) ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type BlockComponent = ComponentType<{ block: any }>;

export const BLOCK_COMPONENT_MAP: Record<string, BlockComponent> =
  Object.fromEntries(
    Array.from(WIDGET_BY_TYPE.entries()).map(([uid, widget]) => [
      uid,
      widgetToBlockComponent(widget),
    ]),
  );

const BlockMapContext =
  createContext<Record<string, BlockComponent>>(BLOCK_COMPONENT_MAP);

export const BlockMapProvider = BlockMapContext.Provider;

export function useBlockMap(): Record<string, BlockComponent> {
  return useContext(BlockMapContext);
}
